import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  HbrDropdown,
  HbrMenu,
  HbrMenuItem,
  HbrMenuDivider,
  HbrMenuLabel,
  HbrButton,
  HbrIcon,
  HbrBadge,
} from '@harbor/elements-react-standalone';
import { gearSix, question, bell } from '@harbor/elements/icons';
import { ReactComponent as CiscoLogo } from '../../navigation/Cisco.svg';
import { NAV_ITEMS } from '../../navigation/appNavConfig';
import RecentsMenuItems from '../../navigation/RecentsMenuItems';
import UserProfileDropdown from '../../navigation/UserProfileDropdown';
import {
  ASSIGNMENT_LEVEL,
  ProductSupportPathQueue,
  useMyProductSupportPathQueues,
} from '../../hooks/useProductSupportPaths';
import { useSalesforceSession } from '../../hooks/useSalesforceSession';
import { RootState } from '../../support/store';
import '../LandingPage/LandingPageV2.scss';
import './PortalShellHeader.scss';

const BETA_PERMISSION = 'page.beta';

type DynamicQueueMenuItem = {
  key: string;
  title: string;
  route: string;
  pageView: string;
  level: number;
};

const getQueueTeamLabel = (queue: ProductSupportPathQueue) => {
  const label =
    queue.team_name ||
    queue.azure_ad_group_name ||
    queue.user_group_name ||
    queue.team_email ||
    queue.title ||
    queue.queueKey;

  return String(label)
    .replace(/^Domain Specialist:\s*/i, '')
    .replace(/^Incubation Team:\s*/i, '')
    .replace(/^Business Unit TME:\s*/i, '')
    .replace(/^Business Unit:\s*/i, '')
    .trim();
};

const APP_FEEDBACK_FORM_URL = 'https://airtable.com/app3Bbmb9vUlCrYVE/pag4pYL3HlPn02lz3/form';

const openAppFeedbackForm = () => {
  window.open(APP_FEEDBACK_FORM_URL, '_blank', 'noopener,noreferrer');
};

const GET_HELP_WEBEX_SPACE_URL = 'webexteams://im?space=85c30920-7df0-11f0-9972-43e0b0e6262e';
const GET_HELP_FALLBACK_URL = 'https://eurl.io/#dhgP65tK_';
const WEBEX_LAUNCH_TIMEOUT_MS = 1500;

const openGetHelp = () => {
  // Browsers can't report whether a custom-protocol launch worked; the page losing focus or
  // visibility within the timeout is the signal that the Webex app took over.
  let appLaunched = false;
  const onLaunch = () => {
    appLaunched = true;
  };
  const onVisibilityChange = () => {
    if (document.hidden) onLaunch();
  };

  window.addEventListener('blur', onLaunch);
  document.addEventListener('visibilitychange', onVisibilityChange);

  window.setTimeout(() => {
    window.removeEventListener('blur', onLaunch);
    document.removeEventListener('visibilitychange', onVisibilityChange);
    if (!appLaunched) window.open(GET_HELP_FALLBACK_URL, '_blank', 'noopener,noreferrer');
  }, WEBEX_LAUNCH_TIMEOUT_MS);

  window.location.assign(GET_HELP_WEBEX_SPACE_URL);
};

interface PortalShellHeaderProps {
  unReadNotificationsCount?: number;
  onOpenReleaseNotes?: (event?: any) => void;
  onOpenDocumentation?: (event?: any) => void;
  isAuthenticated?: boolean;
}

const findNavItem = (key: string) => NAV_ITEMS.find((item) => item.key === key);

const PortalShellHeader: React.FC<PortalShellHeaderProps> = ({
  unReadNotificationsCount = 0,
  onOpenReleaseNotes,
  onOpenDocumentation,
  isAuthenticated = true,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const authz = useSelector((state: RootState) => state.user?.authz);

  const { data: mySupportPathQueues = [] } = useMyProductSupportPathQueues();

  const permSet = useMemo(
    () => new Set<string>(authz?.uiPermissions || []),
    [authz?.uiPermissions]
  );
  const isAdminFull = permSet.has('admin.full');

  const supportPathQueueMenuItems = useMemo<DynamicQueueMenuItem[]>(() => {
    return mySupportPathQueues
      .filter((queue) =>
        [
          ASSIGNMENT_LEVEL.DomainSpecialist,
          ASSIGNMENT_LEVEL.IncubationTeam,
        ].includes(queue.assignment_type_level as any)
      )
      .map((queue) => ({
        key: queue.queueKey,
        title: getQueueTeamLabel(queue),
        route: `/opportunity-team/${queue.queueKey}`,
        pageView: 'page.opportunities.view',
        level: queue.assignment_type_level,
      }))
      .sort((a, b) => {
        if (a.level !== b.level) return a.level - b.level;
        return a.title.localeCompare(b.title);
      });
  }, [mySupportPathQueues]);

  const domainQueueMenuItems = useMemo(() => {
    return supportPathQueueMenuItems.filter(
      (item) => item.level === ASSIGNMENT_LEVEL.DomainSpecialist
    );
  }, [supportPathQueueMenuItems]);

  const incubationQueueMenuItems = useMemo(() => {
    return supportPathQueueMenuItems.filter(
      (item) => item.level === ASSIGNMENT_LEVEL.IncubationTeam
    );
  }, [supportPathQueueMenuItems]);

  const canAccessPage = (pageViewPerm?: string, opts?: { beta?: boolean }) => {
    if (isAdminFull) return true;
    if (!pageViewPerm) return false;
    if (!permSet.has(pageViewPerm)) return false;
    if (opts?.beta && !permSet.has(BETA_PERMISSION)) return false;
    return true;
  };

  const dashboardItem = findNavItem('dashboard');
  const leaderItem = findNavItem('leader');
  const customerItem = findNavItem('customer');
  const opportunityItem = findNavItem('opportunity');
  const workshopsItem = findNavItem('workshops');
  const solutionsItem = findNavItem('solutions');
  const msspItem = findNavItem('mssp');
  const approvalsItem = findNavItem('approvals');
  const adminItem = findNavItem('admin');
  const p2pItem = findNavItem('p2p');
  const cyberBytesItem = findNavItem('cyber-bytes');
  const safeItem = findNavItem('safe');
  const assessmentItem = findNavItem('assessment');
  const campaignItem = findNavItem('campaign');

  const canSeeDashboard = canAccessPage(dashboardItem?.pageView, { beta: dashboardItem?.beta });
  const canSeeLeader = canAccessPage(leaderItem?.pageView, { beta: leaderItem?.beta });
  const canSeeCustomers = canAccessPage(customerItem?.pageView, { beta: customerItem?.beta });
  const canSeeOpportunities = canAccessPage(opportunityItem?.pageView, { beta: opportunityItem?.beta });
  const canSeeWorkshops = canAccessPage(workshopsItem?.pageView, { beta: workshopsItem?.beta });
  const canSeeSolutions = canAccessPage(solutionsItem?.pageView, { beta: solutionsItem?.beta });
  const canSeeMssp = canAccessPage(msspItem?.pageView, { beta: msspItem?.beta });
  const canSeeApprovals = canAccessPage(approvalsItem?.pageView, { beta: approvalsItem?.beta });
  const canSeeP2p = canAccessPage(p2pItem?.pageView, { beta: p2pItem?.beta });
  const canSeeCyberBytes = canAccessPage(cyberBytesItem?.pageView, { beta: cyberBytesItem?.beta });

  // SAFE is customer engagement work, so it needs a live Salesforce session on top of the page
  // permission — the same condition `HasSalesforceSession` enforces on `api/db/safe/*`. Hiding the
  // entries keeps a Cisco-SSO-only user from walking into a wall of 403s.
  const salesforceConnected = useSalesforceSession();
  const canSeeSafe =
    canAccessPage(safeItem?.pageView, { beta: safeItem?.beta }) &&
    (!safeItem?.requiresSalesforce || salesforceConnected);

  // The assessment surface is not gated on Salesforce — an assessment need not belong to a
  // customer account — so the page permission alone decides visibility.
  const canSeeAssessment = canAccessPage(assessmentItem?.pageView, { beta: assessmentItem?.beta });

  // Campaigns are also ungated on Salesforce — a campaign composes enablement journey maps and
  // assessments that need not belong to a customer account — so the page permission alone decides.
  const canSeeCampaign = canAccessPage(campaignItem?.pageView, { beta: campaignItem?.beta });

  // The Admin dropdown opens for a full admin, and also for anyone holding the page permission of
  // one entry inside it. `SAFE Admin` is exactly that case: it grants `page.safeAdmin.view` and
  // nothing else, so gating the whole dropdown on `page.admin.view` would leave the section
  // reachable only by typing the URL.
  const adminSubMenuItems = (adminItem?.subMenu?.items || []).filter((item) =>
    item.pageView ? canAccessPage(item.pageView) : canAccessPage(adminItem?.pageView)
  );
  const canSeeAdmin =
    canAccessPage(adminItem?.pageView, { beta: adminItem?.beta }) || adminSubMenuItems.length > 0;

  const canSeeResources =
    canSeeWorkshops ||
    canSeeSolutions ||
    canSeeMssp ||
    canSeeApprovals ||
    canSeeSafe ||
    canSeeAssessment ||
    canSeeCampaign ||
    canSeeP2p ||
    canSeeCyberBytes;

  const isActive = (...paths: string[]) =>
    paths.some((p) => location.pathname === p || location.pathname.startsWith(`${p}/`));

  const onProfile = location.pathname === '/portal' && new URLSearchParams(location.search).get('view') === 'profile';
  const onPortal = isActive('/portal') && !onProfile;

  return (
    <nav className={`v2__navbar portal-shell-header ${!isAuthenticated ? 'portal-shell-header--dark' : ''}`}>
      <div className="v2__navbar-inner portal-shell-header__inner">
        <div
          className="v2__logo portal-shell-header__brand"
          style={{ cursor: 'pointer' }}
          onClick={() => navigate('/portal')}
        >
          <CiscoLogo style={{ width: 40, height: 35 }} />
          <span>Security Hub</span>
        </div>

        <div className="v2__nav-links portal-shell-header__links">
          {isAuthenticated && (
            <button
              type="button"
              className={`v2__nav-link portal-shell-header__nav-trigger ${onPortal ? 'v2__nav-link--active' : ''}`}
              onClick={() => navigate('/portal')}
            >
              Portal
            </button>
          )}

          {isAuthenticated && (
            <button
              type="button"
              className={`v2__nav-link portal-shell-header__nav-trigger ${onProfile ? 'v2__nav-link--active' : ''}`}
              onClick={() => navigate('/portal?view=profile')}
            >
              Profile
            </button>
          )}

          {(canSeeDashboard || canSeeLeader) && (
            <HbrDropdown hoist menu distance={8}>
              <button
                  slot="trigger"
                  className={`v2__nav-link portal-shell-header__nav-trigger ${
                    isActive('/dashboard', '/leader') ? 'v2__nav-link--active' : ''
                  }`}
                >
                Dashboard <span className="portal-shell-header__caret">▾</span>
              </button>
              <HbrMenu>
                {canSeeDashboard && (
                  <HbrMenuItem onClick={() => navigate('/dashboard')}>My Dashboard</HbrMenuItem>
                )}
                {canSeeLeader && (
                  <HbrMenuItem onClick={() => navigate('/leader')}>Leader View</HbrMenuItem>
                )}
              </HbrMenu>
            </HbrDropdown>
          )}

          {canSeeCustomers && (
            <HbrDropdown hoist menu distance={8}>
              <button
                slot="trigger"
                className={`v2__nav-link portal-shell-header__nav-trigger ${
                  isActive('/customers', '/customer') ? 'v2__nav-link--active' : ''
                }`}
              >
                Customers <span className="portal-shell-header__caret">▾</span>
              </button>
              <HbrMenu>
                <HbrMenuItem onClick={() => navigate('/customers')}>All Customers</HbrMenuItem>
                <RecentsMenuItems
                  variant="menu"
                  baseUrl="/customer"
                  currentRoute={location.pathname}
                  handleClick={(route) => navigate(route)}
                />
              </HbrMenu>
            </HbrDropdown>
          )}

          {canSeeOpportunities && (
            <HbrDropdown hoist menu distance={8}>
              <button
                slot="trigger"
                className={`v2__nav-link portal-shell-header__nav-trigger ${
                  isActive('/opportunity') ? 'v2__nav-link--active' : ''
                }`}
              >
                Opportunities <span className="portal-shell-header__caret">▾</span>
              </button>
              <HbrMenu>
                <HbrMenuItem onClick={() => navigate('/opportunity')}>All Opportunities</HbrMenuItem>

                {domainQueueMenuItems.length > 0 && (
                  <>
                    <HbrMenuDivider />
                    <HbrMenuLabel>Domain</HbrMenuLabel>
                    {domainQueueMenuItems.map((item) => (
                      <HbrMenuItem key={item.route} onClick={() => navigate(item.route)}>
                        {item.title}
                      </HbrMenuItem>
                    ))}
                  </>
                )}

                {incubationQueueMenuItems.length > 0 && (
                  <>
                    <HbrMenuDivider />
                    <HbrMenuLabel>Incubation</HbrMenuLabel>
                    {incubationQueueMenuItems.map((item) => (
                      <HbrMenuItem key={item.route} onClick={() => navigate(item.route)}>
                        {item.title}
                      </HbrMenuItem>
                    ))}
                  </>
                )}

                <RecentsMenuItems
                  variant="menu"
                  baseUrl="/opportunity"
                  currentRoute={location.pathname}
                  handleClick={(route) => navigate(route)}
                />
              </HbrMenu>
            </HbrDropdown>
          )}

          {canSeeResources && (
            <HbrDropdown hoist menu distance={8}>
              <button
                slot="trigger"
                className={`v2__nav-link portal-shell-header__nav-trigger ${
                  isActive('/workshops', '/solutions', '/mssp', '/approvals', '/safe', '/assessments', '/campaigns', '/p2p', '/cyber-bytes') ? 'v2__nav-link--active' : ''
                }`}
              >
                Resources <span className="portal-shell-header__caret">▾</span>
              </button>
              <HbrMenu>
                {canSeeWorkshops && (
                  <HbrMenuItem onClick={() => navigate('/workshops/upcoming-events')}>
                    Workshops
                  </HbrMenuItem>
                )}
                {canSeeSolutions && (
                  <HbrMenuItem onClick={() => navigate('/solutions/architecture')}>
                    Solutions
                  </HbrMenuItem>
                )}
                {canSeeMssp && (
                  <HbrMenuItem onClick={() => navigate('/mssp')}>MSSP</HbrMenuItem>
                )}
                {canSeeApprovals && (
                  <HbrMenuItem onClick={() => navigate('/approvals')}>Approvals</HbrMenuItem>
                )}
                {/*
                  One entry, not two: an assessment belongs to a customer workshop now, so there is
                  no "SAFE Risk" to open — only a list of workshops to pick one from or add to.
                  Note this is not the `/workshops` item above, which is training events.
                */}
                {canSeeSafe && (
                  <HbrMenuItem onClick={() => navigate('/engagements')}>
                    SAFE Workshops
                  </HbrMenuItem>
                )}
                {canSeeAssessment && (
                  <HbrMenuItem onClick={() => navigate('/assessments')}>
                    Assessments
                  </HbrMenuItem>
                )}
                {canSeeCampaign && (
                  <HbrMenuItem onClick={() => navigate('/campaigns')}>
                    Campaigns
                  </HbrMenuItem>
                )}
                {canSeeP2p && (
                  <HbrMenuItem onClick={() => navigate('/p2p')}>P2P</HbrMenuItem>
                )}
                {canSeeCyberBytes && (
                  <HbrMenuItem onClick={() => navigate('/cyber-bytes')}>Cyber Bytes</HbrMenuItem>
                )}
              </HbrMenu>
            </HbrDropdown>
          )}

          {canSeeAdmin && (
            <HbrDropdown hoist menu distance={8}>
              <button
                slot="trigger"
                className={`v2__nav-link portal-shell-header__nav-trigger ${
                  isActive('/admin') ? 'v2__nav-link--active' : ''
                }`}
              >
                Admin <span className="portal-shell-header__caret">▾</span>
              </button>
              <HbrMenu>
                {adminSubMenuItems.map((item) => (
                  <HbrMenuItem key={item.route} onClick={() => item.route && navigate(item.route)}>
                    {item.title}
                  </HbrMenuItem>
                ))}
                <RecentsMenuItems
                  variant="menu"
                  baseUrl="/admin"
                  currentRoute={location.pathname}
                  handleClick={(route) => navigate(route)}
                />
              </HbrMenu>
            </HbrDropdown>
          )}
        </div>

        <div className="portal-shell-header__utilities">
          {unReadNotificationsCount > 0 && (
            <div className="relative inline-block">
              <HbrButton noPadding onClick={onOpenReleaseNotes}>
                <HbrIcon asset={bell} />
              </HbrButton>
              <HbrBadge
                dot
                style={{ '--dot-size': '16px' } as React.CSSProperties}
                size="medium"
                sentiment="danger"
                className="absolute -top-1 -right-1"
              >
                <span className="text-[10px]">{unReadNotificationsCount}</span>
              </HbrBadge>
            </div>
          )}

          {/* Tenant settings and help are authenticated-only; UserProfileDropdown
              self-gates and renders its own logged-out login affordance. */}
          {isAuthenticated && (
            <HbrDropdown hoist menu distance={12} placement="bottom-end">
              <HbrButton noPadding slot="trigger">
                <HbrIcon asset={gearSix} />
              </HbrButton>
              <HbrMenu>
                <HbrMenuItem onClick={() => {}}>Tenant Settings</HbrMenuItem>
              </HbrMenu>
            </HbrDropdown>
          )}

          {isAuthenticated && (
            <HbrDropdown hoist menu distance={12} placement="bottom-end">
              <HbrButton noPadding slot="trigger">
                <HbrIcon asset={question} />
              </HbrButton>
              <HbrMenu>
                <HbrMenuItem onClick={onOpenReleaseNotes} onHbr-click={onOpenReleaseNotes}>
                  Release Notes
                </HbrMenuItem>
                <HbrMenuItem onClick={openAppFeedbackForm}>App Feedback</HbrMenuItem>
                <HbrMenuItem onClick={openGetHelp}>Get Help</HbrMenuItem>
                <HbrMenuItem onClick={onOpenDocumentation} onHbr-click={onOpenDocumentation}>
                  Documentation
                </HbrMenuItem>
              </HbrMenu>
            </HbrDropdown>
          )}

          {isAuthenticated && <HbrMenuDivider vertical />}

          <UserProfileDropdown />
        </div>
      </div>
    </nav>
  );
};

export default PortalShellHeader;
